// src/app/service/auth.service.ts
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { filter, take, tap, catchError, map, switchMap } from 'rxjs/operators'; // Add map, switchMap
import { Observable, of, throwError } from 'rxjs'; // Add of

import { environment } from 'src/environments/environment';
import { paths } from 'src/environments/paths';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly USER_ID_STORAGE_KEY = 'currentAppUserId'; 

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router
  ) { }

  setUserId(userId: string): void {
    localStorage.setItem(this.USER_ID_STORAGE_KEY, userId);
  }

  getUserId(): string | null {
    return localStorage.getItem(this.USER_ID_STORAGE_KEY);
  }

  removeUserId(): void {
    localStorage.removeItem(this.USER_ID_STORAGE_KEY);
  }

  getIsLoggedIn(): boolean {
    return !!this.getUserId();
  }

  /**
   * Sends the retrieved auth code and user ID to the backend to get a session token (cookie).
   * Returns an Observable that completes on success or errors on failure.
   * Does NOT handle navigation itself, allowing the caller (e.g., AuthGuard) to decide.
   * @param userId The user identifier.
   * @param authCode The one-time authentication code.
   */
  public exchangeAuthCodeForSession(userId: string, authCode: string): Observable<any> { // Returns Observable
    const headers = {
      'Content-Type': 'application/json'
    };

    const body = {
      userId: userId,
      authCode: authCode
    };

    return this.http.post(`${environment.baseUrlUserService}/v1/field-officer/otp/webview-token`, body, { headers, withCredentials: true }).pipe(
      tap(response => {
        this.setUserId(userId);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Auth exchange failed:', error);
        let errorMessage = 'Authentication failed. Please try again.';
        if (error.status === 401 || error.status === 403) {
          errorMessage = 'Invalid authentication code or credentials.';
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        }
        this.clearClientSessionData();
        return throwError(() => error);
      })
    );
  }

  public clearClientSessionData(): void {
    this.removeUserId();
    console.log('AuthService: Client-side session data cleared.');
  }

  public handleAuthFailure(message: string): void {
    console.error('Authentication failure:', message);
    alert(message);
    this.router.navigate(['/auth-error']);
  }
}
